import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { KnowledgeSource, KnowledgeSourceConfigType } from '@vendasta/embeddings';
import { AiKnowledgeService } from '../ai-knowledge.service';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MatListModule } from '@angular/material/list';
import { CustomKnowledgeEditorComponent } from '../custom-knowledge-editor/custom-knowledge-editor.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { DeleteCustomKnowledgeComponent } from '../delete-custom-knowledge/delete-custom-knowledge.component';
import { LearnMoreBusinessProfileComponent } from '../learn-more-business-profile/learn-more-business-profile.component';
import { MatCardModule } from '@angular/material/card';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { KnowledgeListComponent } from '../knowledge-list/knowledge-list.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { SHOW_BUSINESS_PROFILE_SOURCE_TOKEN } from '../tokens';
import { AiKnowledgeI18nModule } from '../assets/i18n/ai-knowledge-i18n.module';

@Component({
  selector: 'ai-knowledge-knowledge-source-management',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    GalaxyPageModule,
    MatButtonModule,
    MatIconModule,
    RouterModule,
    MatListModule,
    MatCardModule,
    CustomKnowledgeEditorComponent,
    DeleteCustomKnowledgeComponent,
    LearnMoreBusinessProfileComponent,
    MatDialogModule,
    KnowledgeListComponent,
  ],
  providers: [AiKnowledgeService],
  templateUrl: './knowledge-source-management.component.html',
  styleUrls: ['./knowledge-source-management.component.scss'],
})
export class KnowledgeSourceManagementComponent implements OnInit {
  DIALOG_WIDTH = '660px';
  private readonly dialog = inject(MatDialog);
  private readonly activeRoute = inject(ActivatedRoute);

  readonly knowledgeSources = signal<KnowledgeSource[]>([]);
  private readonly showBusinessProfileSource = toSignal(inject(SHOW_BUSINESS_PROFILE_SOURCE_TOKEN));
  private knowledgeService = inject(AiKnowledgeService);

  readonly isLoading = signal(true);

  async ngOnInit(): Promise<void> {
    await this.refreshSources();
    const editSourceID = this.activeRoute.snapshot.queryParamMap.get('editSource');
    if (editSourceID) {
      const source = this.knowledgeSources().find((s) => s.id == editSourceID);
      if (source) {
        await this.editKnowledgeSource(source);
      }
    }
  }

  async refreshSources(): Promise<void> {
    this.isLoading.set(true);
    const result = await this.knowledgeService.listKnowledgeSources([
      KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_CUSTOM_DATA,
    ]);
    const knowledgeSources = result.knowledgeSources || [];
    if (this.showBusinessProfileSource()) {
      // manually insert business profile source because it may not exist yet
      const businessProfileSource = this.knowledgeService.buildBusinessProfileKnowledgeSource();
      if (businessProfileSource) {
        knowledgeSources.unshift(businessProfileSource);
      }
    }
    this.knowledgeSources.set(knowledgeSources);
    this.isLoading.set(false);
  }

  async addNewKnowledgeSource(): Promise<void> {
    const result = await firstValueFrom(
      this.dialog.open(CustomKnowledgeEditorComponent, { width: this.DIALOG_WIDTH }).afterClosed(),
    );
    if (result) {
      await this.refreshSources();
    }
  }

  async editKnowledgeSource(source: KnowledgeSource): Promise<void> {
    if (source?.config?.businessProfileConfig?.accountGroupId) {
      this.dialog.open(LearnMoreBusinessProfileComponent, {
        width: this.DIALOG_WIDTH,
      });
      return;
    }
    const result = await firstValueFrom(
      this.dialog
        .open(CustomKnowledgeEditorComponent, {
          width: this.DIALOG_WIDTH,
          data: { knowledgeSource: source },
        })
        .afterClosed(),
    );
    if (result) {
      await this.refreshSources();
    }
  }

  async deleteKnowledgeSource(source: KnowledgeSource): Promise<void> {
    const result = await firstValueFrom(
      this.dialog
        .open(DeleteCustomKnowledgeComponent, {
          width: this.DIALOG_WIDTH,
          data: { knowledgeSourceId: source.id },
        })
        .afterClosed(),
    );
    if (result) {
      await this.refreshSources();
    }
  }
}
